import { useRef, useState, useEffect } from "react";
import { Sparkles, Send, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { API_BASE } from "@/lib/api";

const SUGGEST = ["How is the 817 ha SEZ figure different from 3,080 ha?", "Which aviation initiatives are separate?", "What is synthetic in this demo?"];

export default function Assistant() {
  const { assistantOpen: open, setAssistantOpen: setOpen, tour } = useApp();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(`s_${Math.random().toString(36).slice(2, 10)}`);
  const end = useRef(null);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const send = async (q) => {
    const m = (q ?? text).trim();
    if (!m || busy) return;
    setText("");
    setBusy(true);
    setMsgs((x) => [...x, { role: "user", text: m }, { role: "assistant", text: "" }]);
    const append = (d) => setMsgs((x) => { const c = [...x]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + d }; return c; });
    try {
      const res = await fetch(`${API_BASE}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Demo-User": localStorage.getItem("vig_user") || "u_public" },
        body: JSON.stringify({ session_id: sid.current, message: m }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "");
          if (line === "[DONE]") continue;
          try {
            const j = JSON.parse(line);
            append(j.delta || (j.error ? `\n⚠ ${j.error}` : ""));
          } catch { /* partial */ }
        }
      }
    } catch (e) {
      append("Assistant is unavailable right now.");
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button data-testid="assistant-open-btn" onClick={() => setOpen(true)} className={`fixed right-5 z-[1250] flex items-center gap-2 rounded-full border border-[#2DD4BF]/50 bg-[#06142a]/95 px-4 py-3 text-[13px] font-semibold text-[#5EEAD4] shadow-xl hover:bg-[#0B1F38] ${tour.active ? "bottom-[230px]" : "bottom-5"}`}>
          <Sparkles className="h-4 w-4" /> Ask Gateway AI
        </button>
      )}
      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-5 right-5 z-[1250] flex h-[560px] w-[min(400px,92vw)] flex-col rounded-xl border border-slate-500/30 bg-[#06142a]/97 shadow-2xl backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-slate-500/20 px-4 py-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-bold"><Sparkles className="h-4 w-4 text-[#2DD4BF]" />Gateway AI assistant</div>
              <div className="text-[10.5px] text-slate-400">Claude · answers from platform records · not investment advice</div>
            </div>
            <button data-testid="assistant-close-btn" onClick={() => setOpen(false)}><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 space-y-3 overflow-auto p-4 scroll-thin" data-testid="assistant-messages">
            {msgs.length === 0 && (
              <div className="space-y-2">
                <p className="text-[13px] text-slate-300">Ask about initiatives, sources, statuses or the demo data.</p>
                {SUGGEST.map((s) => (
                  <button key={s} data-testid="assistant-suggestion" onClick={() => send(s)} className="block w-full rounded-md border border-slate-500/30 px-3 py-2 text-left text-[12.5px] text-slate-200 hover:border-[#2DD4BF]/60">{s}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`whitespace-pre-wrap rounded-lg px-3 py-2 text-[13px] leading-relaxed ${m.role === "user" ? "ml-8 bg-[#F4B842]/15 text-white" : "mr-4 bg-white/5 text-slate-200"}`}>
                {m.text || (busy && i === msgs.length - 1 ? "Thinking…" : "")}
              </div>
            ))}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t border-slate-500/20 p-3">
            <input data-testid="assistant-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask a question…" className="vig-input" />
            <button data-testid="assistant-send-btn" disabled={busy} className="vig-btn-gold px-3"><Send className="h-4 w-4" /></button>
          </form>
        </div>
      )}
    </>
  );
}
